/**
 * Benutzerverwaltung.
 *
 * Sichtbar nur mit der Faehigkeit `user.manage`. Diese Sichtbarkeit ist
 * Bequemlichkeit, kein Schutz: der Server weist jede Anfrage an /users ohne
 * diese Faehigkeit mit 403 ab, unabhaengig davon, was die Oberflaeche zeigt.
 * Wer die Adresse direkt aufruft, sieht deshalb eine Fehlermeldung und keine
 * Daten.
 */
import { useCallback, useEffect, useState } from "react";
import type { RoleCode, UserListItem } from "@homebase/shared";
import { MIN_PASSWORD_LENGTH, ROLE_CODES } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { useI18n, type I18n, type Language } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import "./Users.css";

/**
 * Datum in der Schreibweise der gewaehlten Sprache.
 * Der gespeicherte Zeitpunkt bleibt davon unberuehrt -- nur die Darstellung
 * folgt der Sprache.
 */
function formatDate(iso: string | null, lang: Language, fallback: string): string {
  if (!iso) return fallback;
  return new Date(iso).toLocaleString(lang === "en" ? "en-GB" : "de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const roleLabel = (i18n: I18n, role: RoleCode): string => i18n.tCode("userRole", role, role);

/**
 * Hervorhebung innerhalb eines uebersetzten Satzes.
 *
 * `mark` klammert einen eingesetzten Wert in ein Steuerzeichen, `emphasise`
 * macht daraus `<strong>`. So bleibt der Satz im Woerterbuch vollstaendig und
 * uebersetzbar, statt in Bruchstuecke zerlegt zu werden.
 */
const MARK = "\u0000";
const mark = (value: string): string => `${MARK}${value}${MARK}`;

function emphasise(text: string): React.ReactNode[] {
  return text.split(MARK).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>);
}

export function Users({ currentUserId }: { currentUserId: number }): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [items, setItems] = useState<readonly UserListItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | "new" | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setItems((await api.users()).items); }
    catch (e) { setError(e); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function change(id: number, patch: Parameters<typeof api.updateUser>[1], message: string): Promise<void> {
    setBusy(id); setError(null); setNotice(null);
    try {
      await api.updateUser(id, patch);
      setNotice(message);
      await load();
    } catch (e) {
      setError(e);
    } finally { setBusy(null); }
  }

  async function resetPassword(u: UserListItem): Promise<void> {
    // Bewusst ueber prompt() und nicht ueber ein Eingabefeld im Formular:
    // so steht das Passwort in keinem React-Zustand, der beim Rendern oder in
    // einem Entwicklerwerkzeug sichtbar bleibt.
    const pw = window.prompt(t("users.promptPassword", { name: u.displayName, n: MIN_PASSWORD_LENGTH }));
    if (pw === null) return;
    await change(u.id, { password: pw }, t("users.msgPasswordSet", { user: u.username }));
  }

  if (error !== null && !items) {
    return <div className="users"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!items) return <div className="users"><LoadingState label={t("users.loading")} /></div>;

  return (
    <div className="users">
      <div className="users__head">
        <h1 className="users__title">{t("users.title")}</h1>
        <button className="btn btn--primary" onClick={() => setCreating((v) => !v)}>
          {creating ? t("common.cancel") : t("users.create")}
        </button>
      </div>

      {/* Der Hinweis steht als GANZER Satz im Woerterbuch, mit Platzhaltern
          fuer die beiden Rollennamen. Er wird nicht in Halbsaetze zerschnitten
          -- die liessen sich in einer anderen Sprache nicht zusammensetzen.
          Hervorgehoben wird erst beim Rendern. */}
      <p className="users__hint">
        {emphasise(t("users.hint", {
          admin: mark(t("userRole.admin")), viewer: mark(t("userRole.viewer")),
        }))}
      </p>

      {creating && <CreateForm busy={busy === "new"} onCancel={() => setCreating(false)}
        onCreate={async (input) => {
          setBusy("new"); setError(null); setNotice(null);
          try {
            await api.createUser(input);
            setNotice(t("users.msgCreated", { user: input.username }));
            setCreating(false);
            await load();
          } catch (e) {
            setError(e);
          } finally { setBusy(null); }
        }} />}

      {notice && <p className="users__notice" role="status">{notice}</p>}
      {error !== null && <p className="users__error" role="alert">{errorText(i18n, error)}</p>}

      {/*
        * DIE TABELLE SCROLLT, DIE SEITE NICHT (§34).
        *
        * Gemessen: die Spaltenkoepfe und die Statusmarken tragen
        * white-space: nowrap, und damit ist diese Tabelle 855px breit,
        * ganz gleich wie schmal das Fenster ist. Unter etwa 600px
        * schob sie die SEITE waagerecht -- und eine waagerecht
        * verschobene Seite nimmt die Hauptnavigation mit.
        *
        * Das ist dasselbe Muster wie in der Bereinigung, im
        * Lieferantenportal und in der Logistik: ein Rahmen mit
        * overflow-x: auto. Kein overflow: hidden -- die Spalten sollen
        * erreichbar bleiben, nur eben durch Scrollen IN der Tabelle.
        */}
      <div className="users__tablewrap">
      <table className="users__table">
        <thead>
          <tr>
            <th>{t("users.colUsername")}</th><th>{t("users.colName")}</th>
            <th>{t("users.colRole")}</th><th>{t("users.colStatus")}</th>
            <th>{t("users.colLastLogin")}</th><th>{t("users.colSessions")}</th>
            <th>{t("users.colActions")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((u) => {
            const self = u.id === currentUserId;
            const disabled = busy === u.id;
            return (
              <tr key={u.id} className={u.isActive ? "" : "users__row--inactive"}>
                <td className="users__mono">
                  {u.username}{self && <span className="users__self"> {t("users.self")}</span>}
                </td>
                <td>{u.displayName}</td>
                <td>
                  <select className="users__select" value={u.role ?? ""} disabled={disabled || self}
                    aria-label={t("users.roleAria", { user: u.username })}
                    onChange={(e) => void change(u.id, { role: e.target.value as RoleCode },
                      t("users.msgRoleChanged", {
                        user: u.username, role: roleLabel(i18n, e.target.value as RoleCode),
                      }))}>
                    {u.role === null && <option value="">{t("users.noRole")}</option>}
                    {/*
                      * DIE LISTE KOMMT AUS `ROLE_CODES` UND NICHT VON HAND.
                      *
                      * Hier standen zwei feste Eintraege. Mit den drei Rollen
                      * aus 0056 waere die Auswahl stehengeblieben, waehrend
                      * Datenbank und Server sie laengst kennen -- und niemand
                      * haette ein Konto auf `product` setzen koennen, ohne die
                      * API von Hand aufzurufen.
                      */}
                    {ROLE_CODES.map((r) => (
                      <option key={r} value={r}>{i18n.tCode("userRole", r)}</option>
                    ))}
                  </select>
                </td>
                <td>{u.isActive
                  ? <span className="users__state users__state--on">{t("users.active")}</span>
                  : <span className="users__state users__state--off">{t("users.inactive")}</span>}</td>
                <td className="users__muted">{formatDate(u.lastLoginAt, i18n.lang, t("users.never"))}</td>
                <td className="users__muted">{u.activeSessions}</td>
                <td className="users__actions">
                  <button className="btn" disabled={disabled} onClick={() => void resetPassword(u)}>
                    {t("users.setPassword")}
                  </button>
                  <button className="btn" disabled={disabled || self}
                    onClick={() => void change(u.id, { isActive: !u.isActive },
                      u.isActive
                        ? t("users.msgDeactivated", { user: u.username })
                        : t("users.msgActivated", { user: u.username }))}>
                    {u.isActive ? t("users.deactivate") : t("users.activate")}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      <p className="users__footnote">{t("users.footnote")}</p>
    </div>
  );
}

function CreateForm({ busy, onCreate, onCancel }: {
  busy: boolean;
  onCreate: (input: { username: string; displayName: string; role: RoleCode; password: string }) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<RoleCode>("viewer");
  const [password, setPassword] = useState("");
  const i18n = useI18n();
  const t = i18n.t;

  const ready = username.length >= 3 && displayName.length >= 2 && password.length >= MIN_PASSWORD_LENGTH;

  return (
    <form className="users__form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || busy) return;
      onCreate({ username: username.trim().toLowerCase(), displayName: displayName.trim(), role, password });
      setPassword("");
    }}>
      <div className="users__field">
        <label htmlFor="nu-user">{t("users.formUsername")}</label>
        <input id="nu-user" value={username} autoComplete="off" spellCheck={false}
          onChange={(e) => setUsername(e.target.value)} placeholder={t("users.formUsernameHint")} />
      </div>
      <div className="users__field">
        <label htmlFor="nu-name">{t("users.formDisplayName")}</label>
        <input id="nu-name" value={displayName} autoComplete="off"
          onChange={(e) => setDisplayName(e.target.value)} placeholder={t("users.formDisplayNameHint")} />
      </div>
      <div className="users__field">
        <label htmlFor="nu-role">{t("users.formRole")}</label>
        <select id="nu-role" value={role} onChange={(e) => setRole(e.target.value as RoleCode)}>
          {ROLE_CODES.map((r) => (
            <option key={r} value={r}>{i18n.tCode("userRole", r)}</option>
          ))}
        </select>
      </div>
      <div className="users__field">
        <label htmlFor="nu-pw">{t("users.formPassword")}</label>
        {/* new-password verhindert, dass der Browser hier gespeicherte
            Zugangsdaten des angemeldeten Administrators einsetzt. */}
        <input id="nu-pw" type="password" value={password} autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("users.formPasswordHint", { n: MIN_PASSWORD_LENGTH })} />
      </div>
      <div className="users__form-actions">
        <button type="submit" className="btn btn--primary" disabled={!ready || busy}>
          {busy ? t("users.formSubmitBusy") : t("users.formSubmit")}
        </button>
        <button type="button" className="btn" onClick={onCancel}>{t("common.cancel")}</button>
      </div>
    </form>
  );
}
